import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { debounce } from 'lodash'
import { SearchItem, useGlobalSearchData } from './useGlobalSearchData'
import { refineSearchResults } from './searchFilters'
import { SearchResultItem } from './SearchResultItem'
import { WemoloCrossIcon, WemoloInput, WemoloSearchIcon, WemoloTypography } from '../../components/Wemolo'
import styles from './GlobalSearch.module.css'

export const GlobalSearchContainer = () => {
    const resultRefs = useRef<(HTMLLIElement | null)[]>([])
    const [searchTerm, setSearchTerm] = useState('')
    const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('')
    const [selectedIndex, setSelectedIndex] = useState(-1)

    const debouncedSetSearchTerm = useCallback(
        debounce((value: string) => {
            setDebouncedSearchTerm(value)
        }, 300),
        []
    )

    useEffect(() => {
        debouncedSetSearchTerm(searchTerm)
        return () => {
            debouncedSetSearchTerm.cancel()
        }
    }, [searchTerm, debouncedSetSearchTerm])

    const { searchData: serverResults, isLoading } = useGlobalSearchData(debouncedSearchTerm)

    const refinedResults = useMemo(() => {
        if (!debouncedSearchTerm || serverResults.length === 0) return serverResults
        return refineSearchResults(serverResults, debouncedSearchTerm)
    }, [serverResults, debouncedSearchTerm])

    useEffect(() => {
        setSelectedIndex(-1)
    }, [debouncedSearchTerm])

    useEffect(() => {
        if (selectedIndex >= 0 && resultRefs.current[selectedIndex]) {
            resultRefs.current[selectedIndex]?.scrollIntoView({
                block: 'nearest',
                behavior: 'smooth'
            })
        }
    }, [selectedIndex])

    const handleSelect = (item: SearchItem) => {
        item.navigateTo()
        setSearchTerm('')
        setDebouncedSearchTerm('')
        setSelectedIndex(-1)
    }

    const handleClearSearch = useCallback(() => {
        setSearchTerm('')
        setDebouncedSearchTerm('')
        debouncedSetSearchTerm.cancel()
        setSelectedIndex(-1)
    }, [debouncedSetSearchTerm])

    const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Escape') {
            e.preventDefault()
            handleClearSearch()
            return
        }
        if (refinedResults.length === 0) return

        switch (e.key) {
            case 'ArrowDown':
                e.preventDefault()
                setSelectedIndex((prev) => (prev < refinedResults.length - 1 ? prev + 1 : prev))
                break
            case 'ArrowUp':
                e.preventDefault()
                setSelectedIndex((prev) => (prev > 0 ? prev - 1 : -1))
                break
            case 'Enter':
                e.preventDefault()
                if (selectedIndex >= 0 && selectedIndex < refinedResults.length) {
                    handleSelect(refinedResults[selectedIndex])
                } else if (refinedResults.length > 0) {
                    handleSelect(refinedResults[0])
                }
                break
        }
    }

    return (
        <div className={styles.root} onKeyDown={handleKeyDown}>
            <WemoloInput
                autoFocus
                placeholder="Search for lots, devices, or stations..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                leftIcon={<WemoloSearchIcon />}
                rightIcon={searchTerm ? <WemoloCrossIcon /> : undefined}
                rightIconAriaLabel="Clear search"
                onRightIconClick={searchTerm ? handleClearSearch : undefined}
            />
            {searchTerm && (
                <div className={styles.dropdown}>
                    {isLoading || searchTerm !== debouncedSearchTerm ? (
                        <WemoloTypography variant="body-small" tag="p" className={styles.status}>
                            {isLoading ? 'Loading...' : 'Searching...'}
                        </WemoloTypography>
                    ) : refinedResults.length > 0 ? (
                        <ul className={styles.list}>
                            {refinedResults.map((item, index) => (
                                <SearchResultItem
                                    key={`${item.type}-${item.id}`}
                                    item={item}
                                    index={index}
                                    selectedIndex={selectedIndex}
                                    onSelect={handleSelect}
                                    resultRef={(el) => (resultRefs.current[index] = el)}
                                />
                            ))}
                        </ul>
                    ) : (
                        <WemoloTypography variant="body-small" tag="p" className={styles.status}>
                            No results found.
                        </WemoloTypography>
                    )}
                </div>
            )}
        </div>
    )
}
